import {AlertTriangle, X} from 'lucide-react';
import {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {
  Alert,
  AlertAction,
  AlertDescription,
  AlertTitle,
} from '@/components/ui/alert';
import {IconButton} from '@/components/ui/icon-button';
import {useGroups} from '@/hooks/useAuth';
import {useEntitledForClient} from '@/hooks/useEntitlements';
import {graphqlClient} from '@/services/graphqlClient';
import {
  useGetMyWorkTimeStatusQuery,
  useStartMyWorkTimeShiftMutation,
  WorkTimeSessionState,
} from '@/services/graphqlHooks';
import queryClient from '@/services/queryClient';

import {useForceEmployeeWorkTimeTrackingConfig} from '../hooks/useEmployeeWorkTimeTrackingForced';
import {shouldShowWorkTimeTrackingReminder} from '../utils/workTimeGroups';

function WorkTimeTrackingBanner() {
  const {t} = useTranslation();
  const groups = useGroups() || [];
  const isForceTrackingEnabled = useForceEmployeeWorkTimeTrackingConfig();
  const shouldShowReminder = shouldShowWorkTimeTrackingReminder(
    groups,
    isForceTrackingEnabled
  );
  const isWorkTimeEntitled = useEntitledForClient('WORK_TIME');
  const workTimeStatus = useGetMyWorkTimeStatusQuery(graphqlClient, undefined, {
    enabled: isWorkTimeEntitled && shouldShowReminder,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  const startWork = useStartMyWorkTimeShiftMutation(graphqlClient);
  const isWorking =
    workTimeStatus.data?.getMyWorkTimeStatus.state ===
    WorkTimeSessionState.Working;

  const [isDismissed, setIsDismissed] = useState(false);

  // Let the reminder show again next time work isn't tracked, rather than
  // staying silenced forever after a single dismissal.
  useEffect(() => {
    if (isWorking) setIsDismissed(false);
  }, [isWorking]);

  const handleStartWork = async () => {
    await startWork.mutateAsync({});
    await queryClient.invalidateQueries({
      queryKey: useGetMyWorkTimeStatusQuery.getKey(),
    });
  };

  if (
    !isWorkTimeEntitled ||
    !shouldShowReminder ||
    workTimeStatus.isLoading ||
    isWorking ||
    isDismissed
  ) {
    return null;
  }

  return (
    <div className="px-5 pb-2 max-sm:px-3">
      <Alert variant="error" className="pr-10">
        <AlertTriangle />
        <AlertTitle>{t('Work is not tracked')}</AlertTitle>
        <AlertDescription>
          {t(
            'You are not tracking your shift. Click Start work to track your shift.'
          )}
        </AlertDescription>
        <AlertAction
          type="button"
          disabled={startWork.isPending}
          onClick={() => void handleStartWork()}
        >
          {t('Start work')}
        </AlertAction>
        <IconButton
          variant="warning"
          size="small"
          className="absolute top-2 right-2"
          onClick={() => setIsDismissed(true)}
          aria-label={t('Close')}
        >
          <X className="size-4" />
        </IconButton>
      </Alert>
    </div>
  );
}

export default WorkTimeTrackingBanner;
